import React, { useEffect } from 'react';
import { useMeasure } from 'react-use';
import { SkeletonLabel } from '@noths/polaris-client-ribbons-design-system';
import getConfig from 'next/config';
import Script from 'next/script';

import { NextConfig } from 'src/types/nextConfig';
import * as styles from '../styles/ProductInfo.styles';

interface KlarnaProps {
  countryCode?: string;
  totalPrice: number;
}

const {
  publicRuntimeConfig: { klarna },
} = getConfig() as NextConfig;

export const KLARNA_APPROXIMATE_HEIGHT = 70;
export const KLARNA_TEST_ID = 'klarna-container';

export const Klarna = ({ countryCode, totalPrice }: KlarnaProps) => {
  /*
   * There is a delay from when the Klarna script has loaded and the script populating the klarna-placement node.
   * Because of this, we cannot rely just on the onLoad callback from the script, we also need to detect when the
   * content has been appended so that we can remove the skeleton loader. Solutions using MutationObserver were not
   * working properly because the content is appended using a closed shadow dom sub tree, and therefore changes are
   * not transparent to the parent dom. The below ref is used to check the height of the wrapping element. When that
   * height is not zero, then the content has been populated.
   */
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
    window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
  }, [totalPrice]);

  if (!countryCode) return null;

  return (
    <div data-testid={KLARNA_TEST_ID} css={styles.klarnaContainer}>
      <Script src={klarna.scriptSrc} strategy="afterInteractive" data-client-id={klarna.id} />
      {height === 0 && (
        <SkeletonLabel height={KLARNA_APPROXIMATE_HEIGHT} css={styles.klarnaSkeletonLoader} />
      )}
      <div ref={ref}>
        <klarna-placement
          data-key="credit-promotion-auto-size"
          data-locale={`en-${countryCode}`}
          data-purchase-amount={totalPrice}
        />
      </div>
    </div>
  );
};
